import { useEffect, useId, useRef, useState } from "react";
import { LocationIcon, SearchIcon } from "@/components/icons";
import { suggestLocations, type LocationSuggestion } from "@/src/api";

type SuggestionState = "idle" | "loading" | "ready" | "empty" | "error";

export function SearchBox({
  query = "",
  location = "",
  compact = false,
}: {
  query?: string;
  location?: string;
  compact?: boolean;
}) {
  const inputId = useId();
  const menuId = `${inputId}-suggestions`;
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const controllerRef = useRef<AbortController | null>(null);
  const [locationValue, setLocationValue] = useState(location);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [suggestionState, setSuggestionState] = useState<SuggestionState>("idle");
  const [activeIndex, setActiveIndex] = useState(-1);

  const menuOpen = suggestionState !== "idle";

  useEffect(() => () => {
    window.clearTimeout(timerRef.current);
    controllerRef.current?.abort();
  }, []);

  function closeSuggestions() {
    setSuggestionState("idle");
    setActiveIndex(-1);
  }

  async function loadSuggestions(value: string) {
    const query = value.trim();
    if (!query) {
      controllerRef.current?.abort();
      setSuggestions([]);
      closeSuggestions();
      return;
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setSuggestionState("loading");

    try {
      const nextSuggestions = await suggestLocations(query, controller.signal);
      if (controllerRef.current !== controller) return;
      setSuggestions(nextSuggestions);
      setActiveIndex(-1);
      setSuggestionState(nextSuggestions.length ? "ready" : "empty");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (controllerRef.current === controller) {
        setSuggestions([]);
        setActiveIndex(-1);
        setSuggestionState("error");
      }
    }
  }

  function scheduleSuggestions(value: string, delay = 160) {
    window.clearTimeout(timerRef.current);
    if (!value.trim()) {
      controllerRef.current?.abort();
      setSuggestions([]);
      closeSuggestions();
      return;
    }
    timerRef.current = window.setTimeout(() => void loadSuggestions(value), delay);
  }

  function selectSuggestion(suggestion: LocationSuggestion) {
    setLocationValue(suggestion.name);
    setSuggestions([]);
    closeSuggestions();
    inputRef.current?.focus();
  }

  function moveActive(direction: 1 | -1) {
    if (!suggestions.length) return;
    setActiveIndex((current) => current < 0
      ? (direction === 1 ? 0 : suggestions.length - 1)
      : (current + direction + suggestions.length) % suggestions.length);
  }

  return (
    <form className={`search-form ${compact ? "search-form--compact" : ""}`} action="/jobs" method="get" role="search">
      <label className="search-field">
        <span className="sr-only">Role, skill, or company</span>
        <SearchIcon />
        <input name="q" defaultValue={query} placeholder="Role, skill, or company" autoComplete="off" maxLength={120} />
      </label>
      <span className="search-form__rule" aria-hidden="true" />
      <div
        className="search-field search-field--location search-location"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) closeSuggestions();
        }}
      >
        <label className="sr-only" htmlFor={inputId}>City, town, state, or remote</label>
        <LocationIcon />
        <input
          ref={inputRef}
          id={inputId}
          name="location"
          value={locationValue}
          placeholder="City, town, state, or remote"
          autoComplete="off"
          maxLength={120}
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-busy={suggestionState === "loading"}
          aria-controls={menuId}
          aria-activedescendant={activeIndex >= 0 ? `${menuId}-${activeIndex}` : undefined}
          onChange={(event) => {
            const value = event.target.value;
            setLocationValue(value);
            scheduleSuggestions(value);
          }}
          onFocus={() => {
            if (locationValue.trim()) scheduleSuggestions(locationValue, 80);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              moveActive(1);
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              moveActive(-1);
            } else if (event.key === "Enter" && activeIndex >= 0) {
              event.preventDefault();
              selectSuggestion(suggestions[activeIndex]);
            } else if (event.key === "Escape") {
              event.preventDefault();
              closeSuggestions();
            }
          }}
        />
        {locationValue ? (
          <button
            className="search-location__clear"
            type="button"
            aria-label="Clear location"
            onClick={() => {
              window.clearTimeout(timerRef.current);
              controllerRef.current?.abort();
              setLocationValue("");
              setSuggestions([]);
              closeSuggestions();
              inputRef.current?.focus();
            }}
          >×</button>
        ) : null}
        {suggestionState === "loading" ? <span className="search-location__spinner" aria-hidden="true" /> : null}
        <div className={`search-location__menu ${menuOpen ? "search-location__menu--open" : ""}`} id={menuId} role="listbox">
          <div className="search-location__menu-head"><strong>Locations in India</strong>{suggestionState === "ready" ? <span>{suggestions.length} matches</span> : null}</div>
          {suggestionState === "ready" ? (
            <div className="search-location__options">
              {suggestions.map((suggestion, index) => (
                <button
                  type="button"
                  id={`${menuId}-${index}`}
                  className={`search-location__option ${activeIndex === index ? "search-location__option--active" : ""}`}
                  role="option"
                  aria-selected={activeIndex === index}
                  key={suggestion.id}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectSuggestion(suggestion)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="search-location__pin" aria-hidden="true"><LocationIcon /></span>
                  <span><strong>{suggestion.name}</strong><small>{suggestion.type === "state" ? "State or union territory · India" : `${suggestion.state ? `${suggestion.state} · ` : ""}India`}</small></span>
                  <i>{suggestion.type === "state" ? "state / ut" : suggestion.type}</i>
                </button>
              ))}
            </div>
          ) : suggestionState !== "idle" ? (
            <div className="search-location__state">
              <strong>{suggestionState === "loading" ? "Finding locations…" : suggestionState === "empty" ? "No Indian location found" : "Locations are temporarily unavailable"}</strong>
              <span>{suggestionState === "loading" ? "Searching cities, towns, and states" : suggestionState === "empty" ? "Try a nearby city, district, or state" : "You can still type a location and search"}</span>
            </div>
          ) : null}
        </div>
      </div>
      <button className="search-form__submit" type="submit">Search jobs</button>
    </form>
  );
}
